import { Input, Directive, ElementRef, ChangeDetectorRef, HostBinding, inject } from '@angular/core'
import { NgElement, WithProperties } from '@angular/elements'
import * as UUID from 'uuid'

/**
 * @public
 */
export type FieldElement = NgElement & WithProperties<FieldComponent>

/**
 * The base of a component that can integrate with native HTML forms.
 *
 * @public
 */
@Directive()
export abstract class FieldComponent {

  protected _changeDetectorRef = inject(ChangeDetectorRef)

  protected abstract _elementRef: ElementRef

  protected _name: string
  protected _value: unknown
  protected _required: boolean
  protected _disabled: boolean
  protected _valid: boolean
  protected _touched: boolean
  protected _wasValidated: boolean

  protected _labelId = UUID.v4() as string

  @Input()
  @HostBinding()
  label: string

  @Input()
  @HostBinding()
  description: string

  @Input()
  @HostBinding()
  name: string

  /**
   * Write the value of the component and mark it as touched.
   *
   * @param value The pending value of the component
   */
  writeValue(value: unknown): void {
    if (value !== null) {
      this.value = value as string
    }
  }

  /**
   * Set the validity state of the component.
   *
   * @param flags   The state of the component
   * @param message The message associated with the validity state
   * @param anchor  The target element where the message will be anchored
   */
  setValidity(flags?: ValidityStateFlags, message?: string, anchor?: HTMLElement): void

  @Input()
  @HostBinding()
  setValidity(...args): void {
    this.internals?.setValidity(...args)
  }

  /**
   * Check whether or not the component is valid.
   *
   * @returns `TRUE` if the component is valid
   */
  @Input()
  @HostBinding()
  checkValidity(): boolean {
    this.wasValidated = true
    this.valid = this.internals?.checkValidity()
    this._changeDetectorRef.detectChanges()
    return this.valid
  }

  /**
   * Check and report whether or not the component is valid.
   *
   * If the component is not valid, it will cancel any submit events in the form, and will display
   * the validation error message.
   *
   * @returns `TRUE` if the component is valid
   */
  @Input()
  @HostBinding()
  reportValidity(): boolean {
    this.wasValidated = true
    this.valid = this.internals?.reportValidity()
    this._changeDetectorRef.detectChanges()
    return this.valid
  }

  get internals(): ElementInternals {
    return this._elementRef.nativeElement.internals
  }

  /**
   * Assign a new value to the control of the component
   *
   * @param value   A raw value that will be normalized before saving
   */
  @Input()
  @HostBinding()
  set value(value: string) {
    if (value !== this._value) {
      this._wasValidated = false
      this._value = value
      this.internals?.setFormValue(value ?? '')
    }
  }

  /**
   * Get the current value from the control of the component
   *
   * @return  Returns a normalized value from the component's control
   */
  get value(): string {
    return this?._value as string ?? null
  }

  /**
   * Whether or not the field is required for the form to be submitted.
   */
  @Input()
  @HostBinding()
  set required(value: string | boolean) {
    let pendingValue = value

    if (typeof pendingValue === 'string') {
      pendingValue = pendingValue.toLowerCase() !== 'false'
    }

    this._required = pendingValue
  }

  get required(): boolean {
    return this._required
  }

  /**
   * Whether or not the component can be interacted with by the user.
   */
  @Input()
  @HostBinding()
  set disabled(value: string | boolean) {
    let pendingValue = value

    if (typeof pendingValue === 'string') {
      pendingValue = pendingValue.toLowerCase() !== 'false'
    }

    this._disabled = pendingValue
  }

  get disabled(): boolean {
    return this._disabled
  }

  /**
   * Determines if the component's value has been touched since its creation.
   */
  @Input()
  @HostBinding()
  set touched(value: string | boolean) {
    let pendingValue = value

    if (typeof pendingValue === 'string') {
      pendingValue = pendingValue.toLowerCase() !== 'false'
    }

    this._touched = pendingValue
  }

  get touched(): boolean {
    return this._touched
  }

  /**
   * Determines if the component's state is valid.
   */
  @Input()
  @HostBinding()
  set valid(value: string | boolean) {
    let pendingValue = value

    if (typeof pendingValue === 'string') {
      pendingValue = pendingValue.toLowerCase() !== 'false'
    }

    this._valid = pendingValue
  }

  get valid(): boolean {
    return this._valid
  }

  /**
   * Determines if the component's state has been validated.
   */
  @Input()
  @HostBinding()
  set wasValidated(value: string | boolean) {
    let pendingValue = value

    if (typeof pendingValue === 'string') {
      pendingValue = pendingValue.toLowerCase() !== 'false'
    }

    this._wasValidated = pendingValue
  }

  get wasValidated(): boolean {
    return this._wasValidated
  }

  @Input()
  @HostBinding()
  get form() {
    return this.internals?.form
  }

  @Input()
  @HostBinding()
  get type() {
    return this._elementRef.nativeElement.localName
  }

  @Input()
  @HostBinding()
  get validity() {
    return this.internals?.validity
  }

  @Input()
  @HostBinding()
  get validationMessage() {
    return this.internals?.validationMessage
  }

  @Input()
  @HostBinding()
  get willValidate() {
    return this.internals?.willValidate
  }

  @Input()
  @HostBinding()
  get labelId(): string {
    return this._labelId
  }

}
